<template>
  <v-autocomplete
    label="Client"
    v-model="client"
    v-model:search="search"
    @update:search="
      (newSearch: string) => debounceFilterClients({ ...queryFilters, page: 1, search: newSearch })
    "
    :items="clients"
    item-value="id"
    item-title="name"
    :loading="isLoading || isCreatePending"
    :error-messages="error && error.message"
    no-filter
    v-bind="$attrs"
  >
    <template #append-item v-if="allowCreateClient">
      <DropdownListItemAction :search="search" @click="addClient()" label="Create Client" />
    </template>
  </v-autocomplete>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue'

import { useCreateClient, useFetchClients } from '@/composables/client'

import type { IClientData } from '@/models/client'
import type { IClientFilterParam } from '@/services'
import { debounce, merge } from 'lodash'
import { useQueryClient } from '@tanstack/vue-query'

import DropdownListItemAction from '@/components/common/DropdownListItemAction.vue'

const client = defineModel<null | IClientData | number>({ default: null })
const search = defineModel<string>('search', { default: '' })

interface Props {
  allowCreateClient?: boolean
}

withDefaults(defineProps<Props>(), {
  allowCreateClient: false
})

const queryFilters = reactive<IClientFilterParam>({
  search: search.value
})

const queryClient = useQueryClient()

const { clients, isLoading, error: fetchError, queryKey } = useFetchClients(queryFilters)

const {
  isPending: isCreatePending,
  error: createError,
  mutate: createClient,
  reset: resetCreateClient
} = useCreateClient()

const error = computed(() => createError.value || fetchError.value)

const debounceFilterClients = debounce(function (filterParams: IClientFilterParam) {
  // mutates filters
  merge(queryFilters, filterParams)
  return queryClient.invalidateQueries({ queryKey: queryKey.value })
}, 200)

function addClient() {
  resetCreateClient()

  const clientData: IClientData = {
    name: search.value.trim()
  }

  createClient(clientData, {
    onSuccess: (newClient) => {
      // Set the newly created client as the selected value
      client.value = newClient.id || newClient
      // Clear the search
      search.value = ''
      // Refresh the client list
      queryClient.invalidateQueries({ queryKey: queryKey.value })
    }
  })
}
</script>
